@file:OptIn(ExperimentalComposeWebApi::class)

package com.siriusxm.pia.components

import androidx.compose.runtime.Composable
import com.siriusxm.pia.SXMUI
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * Stylesheet for skeleton loading placeholders.
 *
 * Skeletons are the grey, animated blocks shown in place of content while it loads,
 * shaped to roughly match the layout that is about to appear. They replace spinners
 * for a more modern "content is coming" feel.
 *
 * The animation is a shimmer sweep: a lighter band slides left-to-right across each
 * block by animating `background-position` over a wide (200%) gradient. Colors are
 * derived from `SXMUI` design tokens so the placeholders adapt to light and dark mode
 * automatically. The sweep is disabled when the user prefers reduced motion.
 *
 * Inject once per page that uses skeletons with `Style(SkeletonStyles)`.
 */
object SkeletonStyles : StyleSheet() {
    /**
     * Keyframe that drives the shimmer sweep by moving the oversized gradient
     * background from its right-hand start position to the left.
     */
    val shimmer by keyframes {
        from {
            property("background-position", "200% 0")
        }
        to {
            property("background-position", "-200% 0")
        }
    }

    /**
     * The base placeholder block: a rounded rectangle filled with a subtle three-stop
     * gradient (base -> lighter highlight -> base) that is twice as wide as the element
     * so the shimmer keyframe can slide the highlight across it.
     */
    val block by style {
        display(DisplayStyle.Block)
        borderRadius(6.px)
        // Base fill and the brighter highlight band, both from theme tokens so the
        // effect works in light and dark mode.
        backgroundColor(SXMUI.borderDividerDefault.value())
        property(
            "background-image",
            "linear-gradient(90deg, " +
                "transparent 0%, " +
                "rgba(255, 255, 255, 0.45) 50%, " +
                "transparent 100%)"
        )
        property("background-size", "200% 100%")
        property("background-repeat", "no-repeat")
        animation(shimmer) {
            duration(1.4.s)
            timingFunction(AnimationTimingFunction.EaseInOut)
            iterationCount(null)
        }
    }

    init {
        // Honor the OS "reduce motion" setting: keep the placeholder visible but hold
        // the highlight still instead of sweeping it.
        media(CSSMediaQuery.Raw("(prefers-reduced-motion: reduce)")) {
            className(block) style {
                property("animation", "none")
            }
        }

        // Dampen the highlight in dark mode, where a bright white band would flash.
        // `block` resolves to the generated class name, matching the `.dark *` pattern
        // used in AppStylesheet.
        (".dark .$block") style {
            property(
                "background-image",
                "linear-gradient(90deg, " +
                    "transparent 0%, " +
                    "rgba(255, 255, 255, 0.10) 50%, " +
                    "transparent 100%)"
            )
        }
    }
}

/**
 * The atomic skeleton primitive: a single shimmering rounded block.
 *
 * Compose these to build placeholders that mirror the shape of loading content.
 *
 * @param width the block width; defaults to filling its container.
 * @param height the block height; defaults to a single text line.
 * @param radius corner radius; pass a large value (or use [SkeletonCircle]) for round shapes.
 * @param styles optional extra style overrides (e.g. margins) applied after the defaults.
 */
@Composable
fun SkeletonBlock(
    width: CSSNumeric = 100.percent,
    height: CSSNumeric = 14.px,
    radius: CSSNumeric = 6.px,
    styles: (StyleScope.() -> Unit)? = null
) {
    Div({
        classes(SkeletonStyles.block)
        style {
            width(width)
            height(height)
            borderRadius(radius)
            styles?.invoke(this)
        }
    })
}

/**
 * A circular skeleton block, useful for avatar/thumbnail placeholders.
 *
 * @param size the diameter of the circle.
 */
@Composable
fun SkeletonCircle(size: CSSNumeric = 40.px) {
    SkeletonBlock(width = size, height = size, radius = 50.percent)
}

/**
 * A multi-line text placeholder. Renders [lines] stacked skeleton blocks at text
 * height with small gaps between them; the final line is rendered shorter so the
 * block reads like a paragraph rather than a solid rectangle.
 *
 * @param lines number of text lines to simulate.
 * @param lineHeight height of each simulated line.
 * @param lastLineWidth width of the final (short) line.
 */
@Composable
fun SkeletonText(
    lines: Int = 3,
    lineHeight: CSSNumeric = 14.px,
    lastLineWidth: CSSNumeric = 60.percent
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
        }
    }) {
        repeat(lines) { index ->
            val isLast = index == lines - 1
            SkeletonBlock(
                width = if (isLast) lastLineWidth else 100.percent,
                height = lineHeight
            )
        }
    }
}

/**
 * A single label/value placeholder as used inside a detail grid: a short, small
 * label block above a slightly wider value block. Mirrors the shape produced by
 * `detail(label) { value }` so the skeleton lines up with the real content.
 */
@Composable
fun SkeletonDetail() {
    Div({
        style {
            marginBottom(10.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(6.px)
        }
    }) {
        SkeletonBlock(width = 40.px, height = 9.px)
        SkeletonBlock(width = 120.px, height = 14.px)
    }
}

/**
 * A grid of [count] [SkeletonDetail] placeholders laid out to match `detailGrid`:
 * three columns that wrap, collapsing to a single column on narrow screens.
 */
@Composable
fun SkeletonDetailGrid(count: Int = 3) {
    Div({
        classes(DetailBoxStyles.detailBox)
    }) {
        repeat(count) {
            Div({ classes(DetailBoxStyles.detailContent) }) {
                SkeletonDetail()
            }
        }
    }
}
